import { Button, Result } from "antd";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Outlet } from "react-router";

import AnalyticsRouteTracker from "@/components/analytics/AnalyticsRouteTracker";
import { analytics } from "@/lib/analytics";
import {
  normalizeErrorName,
  sanitizeErrorMessage,
  sanitizeErrorStack,
  sanitizePathname,
} from "@/lib/analytics/error-utils";
import { captureReactError } from "@/lib/sentry";

const GenericErrorFallback = () => {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <Result
        status="error"
        title="Something went wrong"
        subTitle="Please try reloading the page. If the problem continues, return home and try again."
        extra={[
          <Button key="reload" type="primary" onClick={() => window.location.reload()}>
            Reload page
          </Button>,
          <Button key="home" onClick={() => window.location.assign("/")}>
            Back Home
          </Button>,
        ]}
      />
    </div>
  );
};

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(err: Error): State {
    // Update state so the next render will show the fallback UI.
    console.error("ErrorBoundary caught an error:", err);

    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);
    captureReactError(error, errorInfo);

    analytics.trackError("React ErrorBoundary", sanitizeErrorMessage(error.message), {
      error_name: normalizeErrorName(error),
      error_stack: sanitizeErrorStack(error.stack),
      component_stack: sanitizeErrorStack(errorInfo.componentStack),
      route_path: sanitizePathname(window.location.pathname),
    });
  }

  public render() {
    if (this.state.hasError) {
      return <GenericErrorFallback />;
    }

    return this.props.children;
  }
}

const ErrorBoundaryLayout = () => (
  <ErrorBoundary>
    <AnalyticsRouteTracker />
    <Outlet />
  </ErrorBoundary>
);

export { ErrorBoundary, ErrorBoundaryLayout };
